import { useCallback, useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { Formik, Field, Form, FormikErrors } from 'formik'
import cn from 'classnames'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { skipToken } from '@reduxjs/toolkit/dist/query'

import Modal from 'components/Modal'
import TextBox from 'components/TextBox'
import Loader from 'components/Loader'
import TextBoxReset from 'components/TextBoxReset/TextBoxReset'

import { useGetPosAccountsQuery, useUpdatePosAccountMutation } from 'store/stadiumApi/posAccounts'
import {
  useDeletePosAccountRoleMutation,
  useCreatePosAccountRoleMutation,
  useGetPosAccountRoleQuery,
} from 'store/stadiumApi/employeeRoles'
import { getCurrentOrgName } from 'store/appStateSlice/selectors'
import { useGetVenuesQuery } from 'store/porticoApi/venues'
import { TEditPosAccountFormValues } from 'types/posAccounts'
import { randomNumbersGenerator } from 'utils/general/randomNumbersGenerator'
import { getNestedErrorMessage } from 'utils/general/getNestedErrorMessage'
import { CONVERT_TO_ORGANIZATION_ATTENDANT_MESSAGES } from 'resources/posAccounts'
import { getValidEmployeeId } from 'utils/posAccounts/getValidEmployeeId'

type TProps = {
  onClose: () => void
  uuid: string
}

const EditPosAccount = ({ onClose, uuid }: TProps) => {
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [isManager, setIsManager] = useState<boolean | undefined>(undefined)
  const [roleUuid, setRoleUuid] = useState<string | undefined>(undefined)

  const navigate = useNavigate()

  const orgName = useSelector(getCurrentOrgName)

  const { isLoading: isLoadingVenues } = useGetVenuesQuery()
  const { currentAccount, isLoading: isLoadingCurrentAccount } = useGetPosAccountsQuery(
    { orgName },
    {
      selectFromResult: ({ data, isLoading }) => ({
        currentAccount: data?.filter((account) => account.uuid === uuid)[0],
        isLoading,
      }),
    }
  )
  const { data: posAccountRole, isLoading: isLoadingPosAccountRole } = useGetPosAccountRoleQuery(
    currentAccount?.id ?? skipToken
  )

  const [updateAccount] = useUpdatePosAccountMutation()
  const [createAccountRoles] = useCreatePosAccountRoleMutation()
  const [deleteAccountRoles] = useDeletePosAccountRoleMutation()

  const { id, name, phone, email, pinNumber, employeeId, deactivatedAt, venueUuid } =
    currentAccount ?? {}

  // when deactivatedAt = null then account is activated, when deactivatedAt = timestamp then account is deactivated
  const isDeactivatedAccount = !isEmpty(deactivatedAt)
  // when account is assigned we suposed to display "convert message", disable all inputs and submit button
  const isAssignedAccount = !isEmpty(venueUuid)
  const isLoading = isLoadingVenues || isLoadingCurrentAccount || isLoadingPosAccountRole

  const initialValues: TEditPosAccountFormValues = {
    name: name ?? '',
    phone: phone ?? '',
    email: email ?? '',
    pinNumber: pinNumber ?? '',
    employeeId: employeeId ?? '',
    role: isManager ? 'manager' : 'cart_attendant',
    roleUuid: roleUuid,
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    pinNumber: Yup.string()
      .required('Required')
      .test(
        'pinNumberLength',
        'PIN Code must be exactly 5 numbers',
        function (value: string | undefined) {
          if (!value) return false

          const isTouchedInitialValue = initialValues.pinNumber !== value
          const isValidEditedValue = value.length === 5

          // pinNumber is valid when:
          // 1. is not changed, then we accept less than 5 numbers
          // 2. updated number has exact 5 numbers
          if (!isTouchedInitialValue) return true
          if (isValidEditedValue) return true

          return false
        }
      ),
  })

  const disableUpdateButton = useCallback(
    (dirty: boolean, errors: FormikErrors<TEditPosAccountFormValues>) => {
      const disableButton =
        !dirty || (showValidationErrors && !isEmpty(errors)) || isAssignedAccount

      return disableButton
    },
    [showValidationErrors]
  )

  const onClickSaveButton = useCallback(() => {
    setShowValidationErrors(true)
  }, [])

  useEffect(() => {
    if (!posAccountRole || !posAccountRole?.employeeRoles) return

    setIsManager(posAccountRole?.employeeRoles[0]?.role === 'manager')
    setRoleUuid(posAccountRole?.employeeRoles[0]?.uuid)
  }, [posAccountRole?.employeeRoles[0]?.role, posAccountRole?.employeeRoles[0]?.uuid])

  const onClickConvertOrg = () => {
    const updateAccountValues = {
      venueUuid: null,
      uuid: uuid as string,
      employeeId: employeeId as string,
    }

    updateAccount(updateAccountValues).then(() => {
      navigate('/pos-accounts')
      toast.success('Success!', {
        position: toast.POSITION.BOTTOM_CENTER,
      })
    })
  }

  const onClickDeactivateAccount = () => {
    const currentDate = String(new Date())

    const updateAccountValues = {
      uuid: uuid as string,
      employeeId: employeeId as string,
      deactivatedAt: isDeactivatedAccount ? null : currentDate,
    }

    updateAccount(updateAccountValues).then(() => {
      navigate('/pos-accounts')
      toast.success('Success!', {
        position: toast.POSITION.BOTTOM_CENTER,
      })
    })
  }

  const onSubmit = (
    values: TEditPosAccountFormValues,
    { setFieldError }: { setFieldError: (field: string, errorMsg: string) => void }
  ) => {
    const { name, phone, email, pinNumber, employeeId, role } = values

    const updateAccountValues = {
      name,
      phone,
      email,
      pinNumber,
      employeeId: getValidEmployeeId(employeeId, orgName, name),
      role,
      uuid: currentAccount?.uuid as string,
    }

    updateAccount(updateAccountValues)
      .unwrap()
      .then(() =>
        deleteAccountRoles({
          uuid: roleUuid,
        })
      )
      .then(() =>
        createAccountRoles({
          employee_id: id,
          role: role,
          type: role === 'manager' ? 'ManagerEmployeeRole' : 'AttendantEmployeeRole',
        })
      )
      .then(() => {
        navigate('/pos-accounts')
        toast.success('Success!', {
          position: toast.POSITION.BOTTOM_CENTER,
        })
      })
      .catch(({ data }) => {
        const errorMessage = getNestedErrorMessage(data)

        if (errorMessage.toLowerCase().includes('pin number has already been taken')) {
          setFieldError('pinNumber', errorMessage)
        }
      })
  }

  return (
    <Modal title="Edit POS Account" onClose={onClose} className="-narrow">
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, setFieldValue, values }) => (
            <Form
              className={cn('item-settings -with-double-btn', { '-disabled': isAssignedAccount })}
            >
              {isLoading ? (
                <Loader container={false} />
              ) : (
                <>
                  {isAssignedAccount && (
                    <section className="item-settings__section">
                      {CONVERT_TO_ORGANIZATION_ATTENDANT_MESSAGES.map((message, index) => (
                        <span className="description pb-base -not-disabled" key={index}>
                          {message}
                        </span>
                      ))}
                      <button className="btn -outline" onClick={onClickConvertOrg} type="button">
                        Convert to Organization Attendant
                      </button>
                    </section>
                  )}
                  <section className="item-settings__section">
                    <h3 className="header-title">Info</h3>
                    <div className="text-field">
                      <Field
                        component={TextBox}
                        label="Name"
                        name="name"
                        wrapperClassName="-space-right"
                        showValidationErrors={showValidationErrors}
                        onChange={(value: string) => setFieldValue('name', value)}
                        value={values?.name}
                        error={errors?.name}
                        disabled={isAssignedAccount}
                      />
                    </div>
                    <div className="text-field">
                      <Field
                        component={TextBox}
                        label="Email Address"
                        name="email"
                        showValidationErrors={showValidationErrors}
                        onChange={(value: string) => setFieldValue('email', value)}
                        value={values?.email}
                        error={errors?.email}
                        disabled={isAssignedAccount}
                      />
                    </div>
                    <div className="text-field">
                      <Field
                        component={TextBox}
                        label="Phone Number"
                        name="phone"
                        showValidationErrors={showValidationErrors}
                        onChange={(value: string) => setFieldValue('phone', value)}
                        value={values?.phone}
                        error={errors?.phone}
                        disabled={isAssignedAccount}
                      />
                    </div>
                    <div className="text-field">
                      <Field
                        component={TextBox}
                        label="External ID"
                        name="employeeId"
                        onChange={(value: string) => setFieldValue('employeeId', value)}
                        value={values?.employeeId}
                        error={errors?.employeeId}
                        disabled={isAssignedAccount}
                      />
                    </div>
                    <div className="text-field">
                      <Field
                        component={TextBoxReset}
                        label="PIN Code"
                        name="pinNumber"
                        onChange={(value: string) => setFieldValue('pinNumber', value)}
                        onClick={() =>
                          setFieldValue('pinNumber', String(randomNumbersGenerator(5)))
                        }
                        value={values?.pinNumber}
                        error={errors?.pinNumber}
                        showValidationErrors={showValidationErrors}
                        disabled={isAssignedAccount}
                      />
                    </div>
                    <div className="item-settings__border-break"></div>
                    <section className="item-settings__section ">
                      <h3 className="header-title">Account Type</h3>
                      <div role="group">
                        <div className="radio-button-outer">
                          <div className="radio-button-inner">
                            <Field
                              type="radio"
                              name="role"
                              value="cart_attendant"
                              disabled={isAssignedAccount}
                            />
                          </div>
                          <div className="radio-button-inner">
                            <div className="radio-button-inner__wrapper">
                              <div className="radio-button-inner__title">Cashier</div>
                              <div className="radio-button-inner__description">
                                Unable to void/refund.
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="item-settings__border-break"></div>
                        <div className="radio-button-outer">
                          <div className="radio-button-inner">
                            <Field
                              type="radio"
                              name="role"
                              value="manager"
                              disabled={isAssignedAccount}
                            />
                          </div>
                          <div className="radio-button-inner">
                            <div className="radio-button-inner__wrapper">
                              <div className="radio-button-inner__title">Manager</div>
                              <div className="radio-button-inner__description">
                                Voiding and Refund capabilities. This user is able to approve any
                                POS changes that require a Manager PIN.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                    <div className="item-settings__border-break"></div>
                    <section className="item-settings__section">
                      <div className="content-wrapper">
                        <div className="content-header">Account Status</div>
                        <div className="content-wrapper pb-0">
                          <span
                            className={cn(
                              'items-dot',
                              { '-error': isDeactivatedAccount },
                              { '-success': !isDeactivatedAccount }
                            )}
                          ></span>
                          <span className="content-header">
                            {!isDeactivatedAccount ? 'Active' : 'Deactivated'}
                          </span>
                        </div>
                      </div>
                      {isDeactivatedAccount && (
                        <div className="description-right">
                          Deactivated on {dayjs(deactivatedAt).format('M/DD/YY HH:mma')}
                        </div>
                      )}
                    </section>
                  </section>
                  <div className="btn-wrapper">
                    <button
                      className="btn -update"
                      onClick={onClickSaveButton}
                      disabled={disableUpdateButton(dirty, errors)}
                      type="submit"
                    >
                      Save
                    </button>
                    <button
                      className={cn('btn -outline ', { '-danger': !isDeactivatedAccount })}
                      onClick={onClickDeactivateAccount}
                      type="button"
                    >
                      {!isDeactivatedAccount ? 'Deactivate POS Account' : 'Reactivate POS Account'}
                    </button>
                  </div>
                </>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default EditPosAccount
